import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Plus, AlertCircle, Calendar, ChevronRight, Search } from 'lucide-react';
import MainLayout from '../../layout/MainLayout';
import CreateRelationshipModal from './CreateRelationshipModal';
import RelationshipDetailModal from './RelationshipDetailModal';
import { MentoringRelationship } from '../../../types/admin';
import apiService from '../../../services/api';
import config from '../../../config';
import './AdminRelationships.scss';

const getImageUrl = (imagePath?: string | null): string | null => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  return `${config.api.baseUrl.replace(/\/api\/?$/, '')}/${imagePath}`;
};
const initials = (name: string) =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
const roleLine = (role?: string, company?: string) => {
  const r = (role || '').trim();
  const c = (company || '').trim();
  if (r && c && r.toLowerCase().indexOf(c.toLowerCase()) === -1) return `${r} ${c}`;
  return r || c || '';
};
const eyebrowDate = () => {
  const d = new Date();
  const dia = d.toLocaleDateString('es', { weekday: 'long' });
  const mes = d.toLocaleDateString('es', { month: 'short' }).replace('.', '');
  return `${dia} ${d.getDate()} de ${mes} · Administrador`.toUpperCase();
};

const Avatar: React.FC<{ name: string; image?: string | null }> = ({ name, image }) => {
  const img = getImageUrl(image);
  return (
    <span className="dup-avatar">
      {img ? <img src={img} alt={name} /> : <span className="dup-avatar__ini">{initials(name)}</span>}
    </span>
  );
};

const AdminRelationships: React.FC = () => {
  const navigate = useNavigate();
  const [relationships, setRelationships] = useState<MentoringRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selected, setSelected] = useState<MentoringRelationship | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => { loadRelationships(); }, []);

  const loadRelationships = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.admin.getRelationships();
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setRelationships(data);
    } catch (err: any) {
      console.error('Error loading relationships:', err);
      setError(err.response?.data?.message || 'Error al cargar las duplas');
      setRelationships([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return relationships;
    return relationships.filter(r =>
      `${r.mentor_first_name} ${r.mentor_last_name} ${r.mentee_first_name} ${r.mentee_last_name}`.toLowerCase().includes(q)
    );
  }, [relationships, search]);

  const handleExport = () => {
    const headers = ['Mentor', 'Mentee', 'Estado', 'Fecha Inicio', 'Empresa Mentor', 'Empresa Mentee'];
    const csvContent = [
      headers.join(','),
      ...filtered.map(r => [
        `"${r.mentor_first_name} ${r.mentor_last_name}"`,
        `"${r.mentee_first_name} ${r.mentee_last_name}"`,
        r.status, r.start_date,
        `"${r.mentor_company || ''}"`, `"${r.mentee_company || ''}"`,
      ].join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `duplas-mentoria-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="admin-relationships admin-relationships--loading">
          <div className="loading-spinner">Cargando duplas...</div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="admin-relationships">
        {/* Header estilo referencia */}
        <div className="dup-header">
          <div className="dup-header__info">
            <div className="eyebrow">{eyebrowDate()}</div>
            <h1 className="dup-title">Duplas del <em>programa</em></h1>
            <p className="dup-sub">Directorio de las parejas. Haz clic en una para abrir su ficha.</p>
          </div>
          <div className="dup-header__actions">
            <button className="btn btn--outline" onClick={handleExport} disabled={filtered.length === 0}>
              <Download size={18} /> <span>Exportar</span>
            </button>
            <button className="btn btn--outline" onClick={() => navigate('/admin/sessions/new')}>
              <Calendar size={18} /> <span>Nueva sesión</span>
            </button>
            <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} /> <span>Crear dupla</span>
            </button>
          </div>
        </div>

        {/* Buscador */}
        <div className="dup-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar por mentor o mentee…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={20} /> <span>{error}</span>
          </div>
        )}

        {/* Grid de tarjetas */}
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__icon">👥</div>
            <h3>No se encontraron duplas</h3>
            <p>{search ? 'Intenta ajustar la búsqueda' : 'Comienza creando tu primera dupla de mentoría'}</p>
            {!search && (
              <button className="btn btn--primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={18} /> <span>Crear primera dupla</span>
              </button>
            )}
          </div>
        ) : (
          <div className="dup-grid">
            {filtered.map(r => (
              <button className="dup-card" key={r.id} onClick={() => navigate(`/admin/pdi-progress?rel=${r.id}`)}>
                <div className="dup-card__people">
                  <div className="dup-person">
                    <Avatar name={`${r.mentee_first_name} ${r.mentee_last_name}`} image={(r as any).mentee_image} />
                    <div className="dup-person__info">
                      <div className="dup-person__name">{r.mentee_first_name} {r.mentee_last_name}</div>
                      <div className="dup-person__role">Mentee{roleLine((r as any).mentee_role, r.mentee_company) ? ` · ${roleLine((r as any).mentee_role, r.mentee_company)}` : ''}</div>
                    </div>
                  </div>
                  <div className="dup-person">
                    <Avatar name={`${r.mentor_first_name} ${r.mentor_last_name}`} image={(r as any).mentor_image} />
                    <div className="dup-person__info">
                      <div className="dup-person__name">{r.mentor_first_name} {r.mentor_last_name}</div>
                      <div className="dup-person__role">Mentor{roleLine((r as any).mentor_title, r.mentor_company) ? ` · ${roleLine((r as any).mentor_title, r.mentor_company)}` : ''}</div>
                    </div>
                  </div>
                </div>
                <div className="dup-card__foot">
                  <span className="dup-card__cta">Ver ficha</span>
                  <ChevronRight size={18} />
                </div>
              </button>
            ))}
          </div>
        )}

        {showCreateModal && (
          <CreateRelationshipModal
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => { setShowCreateModal(false); loadRelationships(); }}
          />
        )}

        {selected && (
          <RelationshipDetailModal
            relationship={selected}
            onClose={() => setSelected(null)}
            onSuccess={() => { setSelected(null); loadRelationships(); }}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default AdminRelationships;
